export const MATRIX_MIN_WAGERS = 2;
export const MATRIX_MAX_WAGERS = 11;
export const MATRIX_DEFAULT_ENTRY_FROM = 2;
export const MATRIX_DEFAULT_ENTRY_TO = 10;
export const REFERENCE_ROUNDS_PER_HOUR = 180;
export const REFERENCE_PACE_LABEL = 'Normalized at 180 rounds/hour';

export interface MartingaleGridCell {
  entryDrought: number;
  stopDrought: number;
  wagerCount: number;
}

export function martingaleWagerCount(entryDrought: number, stopDrought: number): number {
  return stopDrought - entryDrought;
}

export function theoreticalFullStopExposure(wagerCount: number, baseStake = 1): number {
  if (!Number.isInteger(wagerCount) || wagerCount < 1) {
    throw new Error('Wager count W must be an integer >= 1.');
  }
  if (!Number.isFinite(baseStake) || baseStake <= 0) {
    throw new Error('baseStake must be a finite number > 0.');
  }
  return baseStake * (2 ** wagerCount - 1);
}

export function theoreticalFullStopLoss(wagerCount: number, baseStake = 1): number {
  return -theoreticalFullStopExposure(wagerCount, baseStake);
}

export function martingaleStakeProgression(wagerCount: number, baseStake = 1): number[] {
  if (!Number.isInteger(wagerCount) || wagerCount < 1) {
    throw new Error('Wager count W must be an integer >= 1.');
  }
  if (!Number.isFinite(baseStake) || baseStake <= 0) {
    throw new Error('baseStake must be a finite number > 0.');
  }
  const stakes: number[] = [];
  for (let attempt = 1; attempt <= wagerCount; attempt += 1) {
    stakes.push(baseStake * 2 ** (attempt - 1));
  }
  return stakes;
}

/** Historical net P&L / theoretical full-stop. Null (N/A) when P&L is negative. Zero P&L is 0. */
export function profitFullStopCoverage(netPnl: number, fullStopExposure: number): number | null {
  if (!Number.isFinite(netPnl) || !Number.isFinite(fullStopExposure) || !(fullStopExposure > 0)) {
    return null;
  }
  if (netPnl < 0) {
    return null;
  }
  return netPnl / fullStopExposure;
}

/** Largest extra full-stop count that keeps cumulative historical P&L >= 0. */
export function extraFullStopsSurvivable(netPnl: number, fullStopExposure: number): number {
  if (!Number.isFinite(netPnl) || !Number.isFinite(fullStopExposure) || !(fullStopExposure > 0) || netPnl < 0) {
    return 0;
  }
  return Math.floor(netPnl / fullStopExposure);
}

export function pnlAfterOneExtraFullStop(netPnl: number, fullStopExposure: number): number {
  return netPnl - fullStopExposure;
}

/** Display-only. Does not change the underlying coverage ratio. */
export function formatProfitFullStopCoverage(coverage: number | null | undefined): string {
  if (coverage === null || coverage === undefined || !Number.isFinite(coverage) || coverage < 0) {
    return 'N/A';
  }
  return `${coverage.toFixed(2)}×`;
}

/** Historical net P&L / theoretical full-stop. Signed; negative P&L stays negative. */
export function profitExposureRatio(netPnl: number, fullStopExposure: number): number | null {
  if (!Number.isFinite(netPnl) || !Number.isFinite(fullStopExposure) || !(fullStopExposure > 0)) {
    return null;
  }
  return netPnl / fullStopExposure;
}

/** Display-only signed ratio. Never N/A for a finite value, including negatives. */
export function formatProfitExposureRatio(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) {
    return '';
  }
  const formatted = ratio.toFixed(2);
  if (Number(formatted) === 0) {
    return '0.00×';
  }
  if (Number(formatted) > 0) {
    return `+${formatted}×`;
  }
  return `${formatted}×`;
}

export type TerminalEvidenceBand = 'UNT' | 'lo' | 'md' | 'hi';

/** Stop-out count overlay only. Does not change P&L / exposure heat. */
export function terminalEvidenceBand(stopOuts: number): TerminalEvidenceBand {
  if (!Number.isFinite(stopOuts) || stopOuts <= 0) {
    return 'UNT';
  }
  if (stopOuts <= 4) {
    return 'lo';
  }
  if (stopOuts <= 9) {
    return 'md';
  }
  return 'hi';
}

export function terminalEvidenceLabel(band: TerminalEvidenceBand): string {
  if (band === 'UNT') {
    return 'terminal event unobserved';
  }
  if (band === 'lo') {
    return 'low terminal evidence';
  }
  if (band === 'md') {
    return 'moderate terminal evidence';
  }
  return 'stronger terminal evidence';
}

/** 0..1 heat weight from |P&L / exposure| relative to the grid's max absolute ratio. */
export function profitExposureHeatWeight(ratio: number, maxAbsRatio: number): number {
  if (!Number.isFinite(ratio) || !(maxAbsRatio > 0)) {
    return 0;
  }
  return Math.min(1, Math.abs(ratio) / maxAbsRatio);
}

/** Break-even stop-out probability for +1 / -(2^W - 1) episodes: 1 / 2^W. Historical only. */
export function breakEvenStopOutRate(wagerCount: number): number {
  if (!Number.isInteger(wagerCount) || wagerCount < 1) {
    throw new Error('Wager count W must be an integer >= 1.');
  }
  return 1 / 2 ** wagerCount;
}

export function isValidMartingaleMatrixCell(
  entryDrought: number,
  stopDrought: number,
  options: { minWagers?: number; maxWagers?: number } = {},
): boolean {
  const minWagers = options.minWagers ?? MATRIX_MIN_WAGERS;
  const maxWagers = options.maxWagers ?? MATRIX_MAX_WAGERS;
  if (!Number.isInteger(entryDrought) || entryDrought < 1) {
    return false;
  }
  if (!Number.isInteger(stopDrought)) {
    return false;
  }
  const wagerCount = martingaleWagerCount(entryDrought, stopDrought);
  return wagerCount >= minWagers && wagerCount <= maxWagers;
}

export function enumerateMartingaleMatrixCells(
  entryFrom = MATRIX_DEFAULT_ENTRY_FROM,
  entryTo = MATRIX_DEFAULT_ENTRY_TO,
  options: { minWagers?: number; maxWagers?: number } = {},
): MartingaleGridCell[] {
  const minWagers = options.minWagers ?? MATRIX_MIN_WAGERS;
  const maxWagers = options.maxWagers ?? MATRIX_MAX_WAGERS;
  if (!Number.isInteger(entryFrom) || !Number.isInteger(entryTo) || entryFrom < 1 || entryTo < entryFrom) {
    throw new Error('Entry drought range must be integers with from <= to and from >= 1.');
  }
  if (!Number.isInteger(minWagers) || !Number.isInteger(maxWagers) || minWagers < 1 || maxWagers < minWagers) {
    throw new Error('Wager bounds must be integers with 1 <= minWagers <= maxWagers.');
  }
  const cells: MartingaleGridCell[] = [];
  for (let entryDrought = entryFrom; entryDrought <= entryTo; entryDrought += 1) {
    for (let wagerCount = minWagers; wagerCount <= maxWagers; wagerCount += 1) {
      cells.push({
        entryDrought,
        stopDrought: entryDrought + wagerCount,
        wagerCount,
      });
    }
  }
  return cells;
}

export interface FrontierPoint {
  entryDrought: number;
  stopDrought: number;
  theoreticalFullStopExposure: number;
  netPnl: number;
  stopOuts: number;
  triggeredEpisodes: number;
}

/**
 * Historical P&L / exposure frontier: no other tested point has both
 * lower-or-equal exposure and higher-or-equal P&L with at least one strict improvement.
 */
export function historicalPnlExposureFrontier(points: readonly FrontierPoint[]): FrontierPoint[] {
  const frontier = points.filter(
    (point) =>
      !points.some(
        (other) =>
          other !== point &&
          other.theoreticalFullStopExposure <= point.theoreticalFullStopExposure &&
          other.netPnl >= point.netPnl &&
          (other.theoreticalFullStopExposure < point.theoreticalFullStopExposure || other.netPnl > point.netPnl),
      ),
  );
  return [...frontier].sort((left, right) => {
    if (left.theoreticalFullStopExposure !== right.theoreticalFullStopExposure) {
      return left.theoreticalFullStopExposure - right.theoreticalFullStopExposure;
    }
    if (left.netPnl !== right.netPnl) {
      return right.netPnl - left.netPnl;
    }
    if (left.entryDrought !== right.entryDrought) {
      return left.entryDrought - right.entryDrought;
    }
    return left.stopDrought - right.stopDrought;
  });
}

export function highestHistoricalPnlAtOrBelowExposure(
  points: readonly FrontierPoint[],
  maxExposure: number,
): FrontierPoint | null {
  const eligible = points.filter((point) => point.theoreticalFullStopExposure <= maxExposure);
  if (eligible.length === 0) {
    return null;
  }
  return [...eligible].sort((left, right) => {
    if (left.netPnl !== right.netPnl) {
      return right.netPnl - left.netPnl;
    }
    if (left.theoreticalFullStopExposure !== right.theoreticalFullStopExposure) {
      return left.theoreticalFullStopExposure - right.theoreticalFullStopExposure;
    }
    if (left.entryDrought !== right.entryDrought) {
      return left.entryDrought - right.entryDrought;
    }
    return left.stopDrought - right.stopDrought;
  })[0] ?? null;
}
